import React, { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { FiSend, FiImage } from "react-icons/fi";
import { FaTimes } from "react-icons/fa";
import moment from "moment";
import { getSupportTicketMessageCountAPI, supportAPI } from "../../../utils/api";
import {
  setMessageHistory,
  setTicketId,
  setTitle,
  setTicketStatus,
  setNewTicketOrReply,
} from "../../../store/slices/supportTicketState";
import "./SupportChat.css";
import InlineLoader from "../../../components/loader/InlineLoader";
import ImageViewer from "../../../components/ImageViewer/ImageViewer";
import { BASE_URL } from "../../../utils/configConstants";

const SupportChat = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  // Get ticket_id from either location.state or URL parameters
  const ticket_id = location.state?.ticket_id || searchParams.get('ticket_id');


  const { messages, chatTitle, ticketStatus } = useSelector(
    (state) => state.supportTicketState
  );

  const [input, setInput] = useState("");
  const [selectedImages, setSelectedImages] = useState([]);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  const [isImageViewVisible, setIsImageViewVisible] = useState(false);
  const [imageViewImages, setImageViewImages] = useState([]);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [userId, setUserId] = useState(null);

  const getUser = async () => {
    const userId = await localStorage.getItem('userId');
    setUserId(userId);
  }
  const updateMessageCount = async () => {
    try {
      const data = {
        support_ticket_id: ticket_id,
        admin_read: 1
      }
      const response = await supportAPI.updateTicket(data);
      if (response?.status == 200) {
        const messageCountResponse = await getSupportTicketMessageCountAPI();
        if (messageCountResponse?.status === 200 && (messageCountResponse?.message_count > 0 || messageCountResponse?.ticket_count > 0)) {
          dispatch(setNewTicketOrReply(1));
        }else{
          dispatch(setNewTicketOrReply(0));
        }
      }
    } catch (error) {
      console.error('Error updating support ticket read status:', error);
    }
  }
  useEffect(() => {
    getUser();
    updateMessageCount();
  }, [])

  // Get chat history using API
  const getSupportChat = async (ticket_id) => {
    try {

      const reqData = {
        ticket_id: ticket_id,
      };
      const response = await supportAPI.getChats(reqData);
      if (response?.status === 200) {
        dispatch(setTicketId(response?.data?._id));
        dispatch(setTitle(response?.data?.subject));
        dispatch(setTicketStatus(response?.data?.status));
        dispatch(setMessageHistory(response?.data?.chat || []));
      } else {
        console.error('Failed to fetch chat history:', response?.message);
      }
    } catch (error) {
      console.error('Error fetching chat history:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!ticket_id) {
      navigate("/supportticket");
      return;
    }

    if (ticket_id) {
      setLoading(true);
      getSupportChat(ticket_id);
    }
  }, [ticket_id, navigate]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  // Image handling functions
  const handleImageSelect = (event) => {
    const files = Array.from(event.target.files);
    const imageFiles = files.filter((file) => file.type.startsWith("image/"));

    if (imageFiles.length > 0) {
      const imagePreviews = imageFiles.map((file) => ({
        file,
        preview: URL.createObjectURL(file),
        name: file.name,
        size: file.size,
      }));
      setSelectedImages((prev) => [...prev, ...imagePreviews]);
    }

    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const removeImage = (index) => {
    setSelectedImages((prev) => {
      const updated = prev.filter((_, i) => i !== index);
      // Revoke object URL to free memory
      URL.revokeObjectURL(prev[index].preview);
      return updated;
    });
  };

  const clearAllImages = () => {
    selectedImages.forEach((img) => URL.revokeObjectURL(img.preview));
    setSelectedImages([]);
  };

  const openImageViewer = (imageUrl) => {
    setImageViewImages([imageUrl]);
    setCurrentImageIndex(0);
    setIsImageViewVisible(true);
  };

  const closeImageViewer = () => {
    setIsImageViewVisible(false);
    setImageViewImages([]);
    setCurrentImageIndex(0);
  };

  const nextImage = () => {
    if (imageViewImages.length > 1) {
      setCurrentImageIndex((prev) => (prev + 1) % imageViewImages.length);
    }
  };

  const previousImage = () => {
    if (imageViewImages.length > 1) {
      setCurrentImageIndex(
        (prev) => (prev - 1 + imageViewImages.length) % imageViewImages.length
      );
    }
  };

  const sendMessage = async () => {
    const hasText = input.trim().length > 0;
    const hasImages = selectedImages.length > 0;

    if (!hasText && !hasImages) return;

    const messageText = input.trim();
    setInput("");

    // Send text message if present
    if (hasText) {
      const reqData = {
        ticket_id: ticket_id,
        message: messageText,
        sender_id: userId,
      };

      try {
        const response = await supportAPI.addMessage(reqData);
        if (response?.status === 200) {
          getSupportChat(ticket_id);
        } else {
          setInput(messageText);
          console.error('Failed to send message:', response?.message);
          return;
        }
      } catch (error) {
        setInput(messageText);
        console.error('Error sending message:', error);
        return;
      }
    }

    // Send images if present
    if (hasImages) {
      setIsUploading(true);
      setUploadProgress(0);

      const totalImages = selectedImages.length;
      let completedImages = 0;

      for (let index = 0; index < selectedImages.length; index++) {
        const imageItem = selectedImages[index];
        try {
          const formData = new FormData();
          formData.append("ticket_id", ticket_id);
          formData.append("sender_id", userId);
          formData.append("image", imageItem.file);

          const response = await supportAPI.addMessage(formData);
          if (response?.status !== 200) {
            console.error("Image upload failed:", response);
          }
        } catch (error) {
          console.error("Error uploading image:", error);
        } finally {
          completedImages++;
          const progress = (completedImages / totalImages) * 100;
          setUploadProgress(progress);

          if (completedImages === selectedImages.length) {
            // Clear images and refresh chat
            clearAllImages();
            setTimeout(() => {
              getSupportChat(ticket_id);
              setIsUploading(false);
              setUploadProgress(0);
            }, 500);
          }
        }
      }
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const renderDateSeparator = useCallback((date) => {
    const messageDate = moment(date);
    const today = moment();
    const yesterday = moment().subtract(1, 'day');

    let displayText;

    if (messageDate.isSame(today, 'day')) {
      displayText = 'Today';
    } else if (messageDate.isSame(yesterday, 'day')) {
      displayText = 'Yesterday';
    } else {
      displayText = messageDate.format("D MMMM");
    }

    return (
      <div key={`date-${date}`} className="date-separator">
        <span className="date-separator-text">{displayText}</span>
      </div>
    );
  }, []);



  const renderMessage = useCallback(
    (item) => {
      const isLoggedInUser = item?.sender_id == userId;
      const messageTime = moment(item?.created_at).format("HH:mm");

      // Get full image URL if image exists
      const imageUrl = item?.image
        ? BASE_URL + item?.image // Replace with actual image URL from your API
        : null;
      // const imageUrl = item?.image
      //   ? `https://picsum.photos/id/237/400/300` // Replace with actual image URL from your API
      //   : null;

      return imageUrl ? (
        <div
          key={item?._id}
          className={`image-message-bubble ${isLoggedInUser ? "image-user-bubble" : "image-support-bubble"
            } `}
        >
          <img
            src={imageUrl}
            alt="Message attachment"
            className="message-image"
            onClick={() => openImageViewer(imageUrl)}
            onError={(e) => {
              e.target.style.display = "none";
              console.error("Failed to load image:", imageUrl);
            }}
          />
          <div className="image-message-time">{messageTime}</div>
        </div>
      ) : (
        <div
          key={item?._id}
          className={`message-bubble ${isLoggedInUser ? "user-bubble" : "support-bubble"
            } `}
        >
          {item?.message && (
            <>
              <p className="message-text">{item?.message}</p>
              <div className="message-time">{messageTime}</div>
            </>
          )}
        </div>
      );
    },
    [userId]
  );

  const renderMessagesWithDateSeparators = useCallback(() => {
    if (!messages || messages.length === 0) {
      return (
        <div className="empty-messages">
          <p>No messages yet. Start the conversation!</p>
        </div>
      );
    }

    const groupedMessages = [];
    let currentDate = null;

    messages.forEach((message, index) => {
      const messageDate = moment(message?.created_at).format("YYYY-MM-DD");

      // Add date separator if this is a new date
      if (currentDate !== messageDate) {
        groupedMessages.push(renderDateSeparator(message?.created_at));
        currentDate = messageDate;
      }

      // Add the message
      groupedMessages.push(renderMessage(message));
    });

    return groupedMessages;
  }, [messages, renderDateSeparator, renderMessage]);

  return (
    <div className="support-chat-container">
      {loading ? (
        <InlineLoader text="Loading chat..." />
      ) : (
        <>
          {/* Messages Section */}
          <div className="support-chat-messages-section">
            <div className="support-chat-messages-container">
              {renderMessagesWithDateSeparators()}
              <div ref={messagesEndRef} />
            </div>
          </div>

          {/* Input Section */}
          <div className="support-chat-input-section">
            {ticketStatus === "closed" && (
              <div className="support-chat-ticket-closed-notice">
                <p>This ticket is closed</p>
              </div>
            )}

            {ticketStatus !== "closed" && (
              <>
                {/* Image Preview Section */}
                {selectedImages.length > 0 && (
                  <div className="image-preview-section">
                    <button
                      className="clear-all-images-btn"
                      onClick={clearAllImages}
                    >
                      <FaTimes size={15} />
                    </button>
                    <div className="support-image-preview-container">
                      {selectedImages.map((imageItem, index) => (
                        <div key={index} className="image-preview-item">
                          <img
                            src={imageItem.preview}
                            alt={`Preview ${index + 1}`}
                            className="image-preview"
                          />
                          <button
                            className="remove-image-btn"
                            onClick={() => removeImage(index)}
                          >
                            <FaTimes size={12} />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Upload Progress */}
                {isUploading && (
                  <div className="upload-progress-section">
                    <div className="upload-progress-bar">
                      <div
                        className="upload-progress-fill"
                        style={{ width: `${uploadProgress}%` }}
                      ></div>
                    </div>
                    <span className="upload-progress-text">
                      Uploading... {Math.round(uploadProgress)}%
                    </span>
                  </div>
                )}

                {/* Input Container */}
                <div className="support-chat-input-container">
                  <button
                    className="action-btn gallery-btn"
                    onClick={() => fileInputRef.current?.click()}
                    title="Select from Gallery"
                  >
                    <FiImage size={20} />
                  </button>

                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    multiple
                    onChange={handleImageSelect}
                    style={{ display: "none" }}
                  />

                  <textarea
                    className="support-chat-message-input"
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    placeholder="Type your message..."
                    onKeyPress={handleKeyPress}
                    rows={1}
                  />

                  <button
                    className={`support-chat-send-btn ${input.trim() || selectedImages.length > 0 ? "active" : ""
                      }`}
                    onClick={sendMessage}
                    disabled={!input?.trim() && selectedImages.length === 0}
                  >
                    <FiSend size={18} />
                  </button>
                </div>
              </>
            )}
          </div>
        </>
      )}

      <ImageViewer
        images={imageViewImages}
        currentIndex={currentImageIndex}
        isVisible={isImageViewVisible}
        onClose={closeImageViewer}
        onNext={nextImage}
        onPrevious={previousImage}
      />
    </div>
  );
};

export default SupportChat;
